import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import { listExamConfigs, generateMeritPosition } from "../../api/examApi";
import "../Attendance/Attendance.css";

const classApi = createLookupApi("classes");
const academicYearApi = createLookupApi("academic-years");

/**
 * `exam-merit-position-generate` ("Merit Generate") — found missing while
 * sweeping the app for real, previously-uncovered gaps (see MIGRATION.md).
 * Direct port of digicampusbd's `MeritPositionGenerateService` — a real,
 * more sophisticated ranking engine than the already-built merit-position
 * *view* pages (`ExamMeritReportsPage`, always a standard 1-2-2-4 ranking by
 * GPA): this one supports Mark-wise or GPA-wise sorting and Sequential
 * (strict 1,2,3,4...) or Non-Sequential (ties share a rank) position
 * assignment, per the exam configuration's own `meritProcessType`, and
 * writes section/shift/group/shift+group/gender positions back to
 * `exam_result` — not just a client-side re-ranking of already-stored data.
 * Also recomputes each subject's real highest score for the exam.
 */
export default function ExamMeritGeneratePage() {
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [examConfigs, setExamConfigs] = useState([]);
  const [examConfigId, setExamConfigId] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    classApi.list().then((list) => {
      setClasses(list);
      if (list.length > 0) setClassId(String(list[0].id));
    });
    academicYearApi.list().then((list) => {
      setAcademicYears(list);
      if (list.length > 0) setAcademicYear(list[0].name);
    });
  }, []);

  useEffect(() => {
    if (!classId) return;
    listExamConfigs(classId).then((configs) => {
      setExamConfigs(configs);
      setExamConfigId(configs.length > 0 ? String(configs[0].examConfigId) : "");
    });
  }, [classId]);

  const handleGenerate = async () => {
    setError("");
    setSuccess("");
    if (!examConfigId || !academicYear) {
      setError("Please select a class, exam, and academic year.");
      return;
    }
    setGenerating(true);
    try {
      const res = await generateMeritPosition(examConfigId, academicYear);
      setSuccess(res.message || "Merit Position Successfully Generated");
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to generate merit position.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Merit Generate</h1>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      <p>
        Recomputes each passed student's section/shift/group/gender merit position for one exam,
        using that exam's own configured ranking rule (mark or GPA based, sequential or tie-sharing).
        See the class-wide/section/group/shift <strong>Merit Reports</strong> pages to view the result.
      </p>

      <div className="attendance-filters">
        <label>
          Class
          <select value={classId} onChange={(e) => setClassId(e.target.value)}>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Exam
          <select value={examConfigId} onChange={(e) => setExamConfigId(e.target.value)}>
            {examConfigs.length === 0 && <option value="">No exams configured</option>}
            {examConfigs.map((ec) => (
              <option key={ec.examConfigId} value={ec.examConfigId}>
                {ec.examName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn-primary" onClick={handleGenerate} disabled={generating}>
          {generating ? "Generating..." : "Generate Merit Position"}
        </button>
      </div>
    </MainLayout>
  );
}
